import { css } from 'lit';

export const styles = css`
	:host {
		/* Sizing */
		--_sds-stepper-step-circle-size: var(--sds-stepper-circle-size, 32px);
		--_sds-stepper-step-border-width: var(--sds-stepper-step-border-width, 2px);
		--_sds-stepper-step-radius: var(--sds-stepper-step-radius, var(--sds-radius-sm));

		/* Layout */
		--_sds-stepper-step-grid-template-rows: var(--sds-stepper-step-grid-template-rows, var(--_sds-stepper-step-circle-size) auto);
		--_sds-stepper-step-grid-template-columns: var(
			--sds-stepper-step-grid-template-columns,
			[line-1-start] 1fr [step-start] auto [line-1-end] var(--_sds-stepper-step-circle-size) [line-2-start] auto [step-end] 1fr [line-2-end]
		);

		/* Button */
		--_sds-stepper-step-step-grid-template-rows: var(--sds-stepper-step-step-grid-template-rows, [circle] var(--_sds-stepper-step-circle-size) [content] auto);
		--_sds-stepper-step-step-grid-template-columns: var(--sds-stepper-step-step-grid-template-columns, [content-start] auto [circle] var(--_sds-stepper-step-circle-size) [content-end] auto);
		--_sds-stepper-step-step-grid-area: var(--sds-stepper-step-step-grid-area, 1 / step / -1);
		--_sds-stepper-step-step-gap: var(--sds-stepper-step-step-gap, var(--sds-spacing-3xs));
		--_sds-stepper-step-step-margin: var(--sds-stepper-step-step-margin, 0);

		/* Divider */
		--_sds-stepper-step-divider-color: var(--sds-stepper-step-divider-color, var(--sds-color-neutral-border-subtle));
		--_sds-stepper-step-divider-width: var(--sds-stepper-step-divider-width, 100%);
		--_sds-stepper-step-divider-height: var(--sds-stepper-step-divider-height, 1px);
		--_sds-stepper-step-divider-min-height: var(--sds-stepper-step-divider-min-height, auto);
		--_sds-stepper-step-divider-grid-row: var(--sds-stepper-step-divider-grid-row, 1);
		--_sds-stepper-step-divider-before-grid-column: var(--sds-stepper-step-divider-before-grid-column, line-1);
		--_sds-stepper-step-divider-before-visibility: var(--sds-stepper-step-divider-before-visibility, visible);
		--_sds-stepper-step-divider-before-display: var(--sds-stepper-step-divider-before-display, block);
		--_sds-stepper-step-divider-after-grid-column: var(--sds-stepper-step-divider-after-grid-column, line-2);
		--_sds-stepper-step-divider-after-visibility: var(--sds-stepper-step-divider-after-visibility, visible);
		--_sds-stepper-step-divider-after-display: var(--sds-stepper-step-divider-after-display, block);

		/* Content */
		--_sds-stepper-step-label-grid-area: var(--sds-stepper-step-label-grid-area, content / 1 / content / -1);
		--_sds-stepper-step-label-text-decoration-thickness: var(--sds-stepper-step-label-text-decoration-thickness, var(--sds-typography-link-text-decoration-thickness));
		--_sds-stepper-step-label-text-underline-offset: var(--sds-stepper-step-label-text-underline-offset, var(--sds-typography-link-text-underline-offset));
		--_sds-stepper-step-label-max-width: var(--sds-stepper-step-label-max-width, 24ch);
		--_sds-stepper-step-label-text-color: var(--sds-stepper-step-label-text-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-label-text-decoration: var(--sds-stepper-step-label-text-decoration, var(--sds-typography-link-text-decoration));

		/* Counter */
		--_sds-stepper-step-counter-grid-area: var(--sds-stepper-step-counter-grid-area, circle / 2);
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-content: var(--sds-stepper-step-counter-content, counters(stepper, ''));
		--_sds-stepper-step-counter-background-color: var(
			--sds-stepper-step-counter-background-color,
			var(--sds-color-background-action-contrast-default, var(--sds-color-main-background-action-contrast-default))
		);
		--_sds-stepper-step-counter-mask: var(--sds-stepper-step-counter-mask, none);
		--_sds-stepper-step-counter-mask-background-color: var(--sds-stepper-step-counter-mask-background-color, transparent);

		/* Focus */
		--_sds-stepper-step-focus-color: var(--sds-stepper-step-focus-color, var(--sds-color-states-focus-outer));
		--_sds-stepper-step-focus-inner-color: var(--sds-stepper-step-focus-inner-color, var(--sds-color-states-focus-inner));
		--_sds-stepper-step-focus-spacing: var(--sds-stepper-step-focus-spacing, var(--sds-color-states-focus-spacing));

		width: fit-content;
	}

	:host(:first-child) {
		--_sds-stepper-step-divider-before-visibility: hidden;
	}

	:host(:last-child) {
		--_sds-stepper-step-divider-after-visibility: hidden;
	}

	.sds-stepper-step {
		display: grid;
		grid-template-columns: var(--_sds-stepper-step-grid-template-columns);
		grid-template-rows: var(--_sds-stepper-step-grid-template-rows);
		gap: var(--sds-spacing-xs);
		justify-items: flex-start;
		flex: 100%;
		padding-block-start: var(--_sds-stepper-step-border-width);
		font-family: var(--sds-typography-font-family);
	}

	.sds-stepper-step__divider {
		display: block;
		align-self: center;
		justify-self: center;

		width: var(--_sds-stepper-step-divider-width);
		height: var(--_sds-stepper-step-divider-height);
		min-height: var(--_sds-stepper-step-divider-min-height);

		grid-row: var(--_sds-stepper-step-divider-grid-row);
		background-color: var(--_sds-stepper-step-divider-color);

		&.sds-stepper-step__divider--before {
			grid-column: var(--_sds-stepper-step-divider-before-grid-column);
			visibility: var(--_sds-stepper-step-divider-before-visibility);
			display: var(--_sds-stepper-step-divider-before-display);
		}

		&.sds-stepper-step__divider--after {
			grid-column: var(--_sds-stepper-step-divider-after-grid-column);
			visibility: var(--_sds-stepper-step-divider-after-visibility);
			display: var(--_sds-stepper-step-divider-after-display);
		}
	}

	.sds-stepper-step__step,
	.sds-stepper-step__step--static {
		display: grid;
		width: 100%;

		grid-area: var(--_sds-stepper-step-step-grid-area);
		grid-template-rows: var(--_sds-stepper-step-step-grid-template-rows);
		grid-template-columns: var(--_sds-stepper-step-step-grid-template-columns);
		place-items: center;

		gap: var(--_sds-stepper-step-step-gap);
		padding: 0;
		margin: var(--_sds-stepper-step-step-margin);

		border: 0;
		background: none;
		border-radius: var(--_sds-stepper-step-radius);
	}

	.sds-stepper-step__step {
		cursor: pointer;

		&:focus-visible {
			box-shadow: 0 0 0 var(--sds-color-states-focus-spacing) var(--_sds-stepper-step-focus-inner-color);
			outline: 3px solid var(--_sds-stepper-step-focus-color);
			outline-offset: 3px;
			isolation: isolate;
		}
	}

	.sds-stepper-step__label {
		grid-column: content;
		grid-area: var(--_sds-stepper-step-label-grid-area);
		min-width: fit-content;
		max-width: var(--_sds-stepper-step-label-max-width);
		text-align: left;

		color: var(--_sds-stepper-step-label-text-color);
		font-family: var(--sds-typography-font-family);
		font-size: var(--sds-typography-body-lg-strong-tall-desktop-font-size);
		font-weight: var(--sds-typography-body-lg-strong-tall-desktop-font-weight);
		line-height: var(--sds-typography-body-lg-strong-tall-desktop-line-height);

		text-decoration: var(--_sds-stepper-step-label-text-decoration);
		text-decoration-thickness: var(--_sds-stepper-step-label-text-decoration-thickness);
		text-underline-offset: var(--_sds-stepper-step-label-text-underline-offset);
	}

	.sds-stepper-step__counter {
		display: flex;
		justify-content: center;
		align-items: center;

		grid-area: var(--_sds-stepper-step-counter-grid-area);

		width: var(--_sds-stepper-step-circle-size);
		height: var(--_sds-stepper-step-circle-size);

		border-radius: 50%;
		border: var(--_sds-stepper-step-border-width) solid var(--_sds-stepper-step-counter-border-color);
		background-color: var(--_sds-stepper-step-counter-background-color);

		counter-increment: stepper;

		color: var(--_sds-stepper-step-counter-text-color);
		font-size: var(--sds-typography-body-md-strong-desktop-font-size);
		font-weight: var(--sds-typography-body-md-strong-desktop-font-weight);
		line-height: var(--sds-typography-body-md-strong-desktop-line-height);

		&::before {
			content: var(--_sds-stepper-step-counter-content);

			display: flex;
			justify-content: center;
			align-items: center;

			width: 100%;
			height: 100%;

			border-radius: 50%;
			background-color: var(--_sds-stepper-step-counter-mask-background-color);
			mask: var(--_sds-stepper-step-counter-mask);
		}
	}

	:host([complete]) {
		--_sds-stepper-step-counter-background-color: var(--sds-stepper-step-counter-background-color, var(--sds-color-action-contrast-subtle, var(--sds-color-main-action-contrast-subtle)));
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-content: var(--sds-stepper-step-counter-content, '');
		--_sds-stepper-step-counter-mask-background-color: var(--sds-stepper-step-counter-mask-background-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-mask: var(
			--sds-stepper-step-counter-mask,
			url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'%3E%3Cpath d='M378-232.43 140.43-470l57.14-57.13L378-346.7l383.43-383.43L818.57-673z'/%3E%3C/svg%3E")
				no-repeat center / 24px 24px
		);
	}

	:host([active]) {
		--_sds-stepper-step-counter-background-color: var(--sds-stepper-step-counter-background-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-action-default, var(--sds-color-main-action-default)));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-main-action-contrast-default));
		--_sds-stepper-step-label-text-decoration: var(--sds-stepper-step-label-text-decoration, none);
		--_sds-stepper-step-label-text-color: var(--sds-stepper-step-label-text-color, var(--sds-color-text-default, var(--sds-color-main-text-default)));

		pointer-events: none;
	}

	:host(:not([active])) .sds-stepper-step__step:hover {
		--_sds-stepper-step-label-text-color: var(--sds-stepper-step-label-text-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
		--_sds-stepper-step-label-text-decoration: var(--sds-stepper-step-label-text-decoration, var(--sds-typography-link-text-decoration));
		--_sds-stepper-step-label-text-decoration-thickness: var(--sds-stepper-step-label-text-decoration-thickness, calc(var(--sds-typography-link-text-decoration-thickness) + 1px));
		--_sds-stepper-step-counter-background-color: var(--sds-stepper-step-counter-background-color, var(--sds-color-action-contrast-subtle, var(--sds-color-main-action-contrast-subtle)));
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
	}

	:host(:not([active])[complete]) .sds-stepper-step__step:hover {
		--_sds-stepper-step-counter-mask-background-color: var(--sds-stepper-step-counter-mask-background-color, var(--sds-color-action-hover, var(--sds-color-main-action-hover)));
		--_sds-stepper-step-counter-mask: var(
			--sds-stepper-step-counter-mask,
			url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'%3E%3Cpath d='M378-232.43 140.43-470l57.14-57.13L378-346.7l383.43-383.43L818.57-673z'/%3E%3C/svg%3E")
				no-repeat center / 24px 24px
		);
	}

	:host(:not([active])) .sds-stepper-step__step:active {
		--_sds-stepper-step-label-text-color: var(--sds-stepper-step-label-text-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
	}

	:host(:not([active])[complete]) .sds-stepper-step__step:active {
		--_sds-stepper-step-counter-mask-background-color: var(--sds-stepper-step-counter-mask-background-color, var(--sds-color-action-active, var(--sds-color-main-action-active)));
		--_sds-stepper-step-counter-mask: var(
			--sds-stepper-step-counter-mask,
			url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'%3E%3Cpath d='M378-232.43 140.43-470l57.14-57.13L378-346.7l383.43-383.43L818.57-673z'/%3E%3C/svg%3E")
				no-repeat center / 24px 24px
		);
	}

	:host([active][complete]) {
		--_sds-stepper-step-counter-mask-background-color: var(--sds-stepper-step-counter-background-color, var(--sds-color-action-contrast-default, var(--sds-color-main-action-contrast-default)));
	}

	:host(:not([active]):not([interactive])) {
		--_sds-stepper-step-label-text-color: var(--sds-stepper-step-label-text-color, var(--sds-color-neutral-text-default));
		--_sds-stepper-step-label-text-decoration: var(--sds-stepper-step-label-text-decoration, none);
		--_sds-stepper-step-counter-background-color: var(--sds-stepper-step-counter-background-color, transparent);
		--_sds-stepper-step-counter-border-color: var(--sds-stepper-step-counter-border-color, var(--sds-color-neutral-border-strong));
		--_sds-stepper-step-counter-text-color: var(--sds-stepper-step-counter-text-color, var(--sds-color-neutral-text-default));
	}
`;
